import { useCallback, useEffect, useMemo, useState } from "react";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import {
  DEFAULT_COMPANY_FORM_CATEGORIES,
  buildCategoryLabels,
} from "./companyFormConstants";

export function useCompanyFormCategories() {
  const [categories, setCategories] = useState(DEFAULT_COMPANY_FORM_CATEGORIES);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const response = await apiUrl.get("/company-forms/categories");
      const rows = response?.data?.data || [];
      if (rows.length > 0) {
        setCategories(
          rows.map((row) => ({
            id: row.id,
            value: row.slug,
            label: row.label,
          })),
        );
      } else {
        setCategories(DEFAULT_COMPANY_FORM_CATEGORIES);
      }
    } catch {
      setCategories(DEFAULT_COMPANY_FORM_CATEGORIES);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const categoryLabels = useMemo(
    () => buildCategoryLabels(categories),
    [categories],
  );

  const createCategory = useCallback(
    async (label) => {
      const trimmed = String(label || "").trim();
      if (!trimmed) {
        throw new Error("Enter a category name.");
      }
      const response = await apiUrl.post("/company-forms/categories", {
        label: trimmed,
      });
      await refresh();
      return response?.data?.data;
    },
    [refresh],
  );

  const updateCategory = useCallback(
    async (id, label) => {
      const trimmed = String(label || "").trim();
      if (!trimmed) {
        throw new Error("Enter a category name.");
      }
      const response = await apiUrl.patch(`/company-forms/categories/${id}`, {
        label: trimmed,
      });
      await refresh();
      return response?.data?.data;
    },
    [refresh],
  );

  const deleteCategory = useCallback(
    async (id) => {
      await apiUrl.delete(`/company-forms/categories/${id}`);
      await refresh();
    },
    [refresh],
  );

  return {
    categories,
    categoryLabels,
    categoriesLoading: loading,
    refreshCategories: refresh,
    createCategory,
    updateCategory,
    deleteCategory,
  };
}
